import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame, Timer, MapPin, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

const LOGO = "https://customer-assets.emergentagent.com/job_korean-food-hub-1/artifacts/ghexjw3c_mokjaa.png";
const HERO_IMG = "https://images.pexels.com/photos/5773961/pexels-photo-5773961.jpeg?auto=compress&cs=tinysrgb&w=1200";

// Names exactly as they appear in the menu — order is the display order
const FEATURED_NAMES = [
  "Korean Fried Chicken",
  "Bibimbap",
  "Tteok-Bokki",
  "Jajangmyeon",
  "Bulgogi Beef",
  "Kimchi Jjigae",
];

const WHY = [
  { icon: Timer, title: "Fast Delivery", desc: "Hot meals arrive within 30 minutes in Dimapur." },
  { icon: Flame, title: "Fresh Korean Food", desc: "Authentic recipes, made-to-order, every time." },
  { icon: MapPin, title: "Live Order Tracking", desc: "Watch your rider on a live map in real time." },
  { icon: ShieldCheck, title: "Easy OTP Login", desc: "No passwords. Sign in with just your email." },
];

export default function LandingPage() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/menu").then(({ data }) => {
      const byName = new Map((data.products || []).map((p) => [p.name, p]));
      const list = FEATURED_NAMES
        .map((n) => byName.get(n))
        .filter(Boolean)
        .map((p) => ({
          name: p.name,
          price: p.price,
          img: p.image,
        }));
      setFeatured(list);
    }).catch(() => setFeatured([]));
  }, []);
  return (
    <div className="bg-white" data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative mj-fade-up">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-[#E8E2D2] -z-0" aria-hidden />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-gray-500 mb-6" data-testid="hero-overline">
                <img src={LOGO} alt="" className="w-5 h-5 rounded-full" />
                먹자 · Est. 2022 · Dimapur
              </div>
              <h1 className="font-display font-black tracking-tighter leading-[0.9] text-6xl sm:text-7xl lg:text-8xl" data-testid="hero-title">
                Let's<br />
                <span className="italic">Eat!</span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-md leading-relaxed">
                Korean comfort food, hand-crafted in Dimapur. Kimbap, Bibimbap, Ramyeon and more — delivered hot, delivered fresh.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/menu"
                  className="bg-black text-white rounded-full px-7 py-4 font-bold inline-flex items-center gap-2 hover:bg-gray-800 active:scale-[0.98] transition"
                  data-testid="hero-order-btn"
                >
                  Order Now <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/menu"
                  className="border-2 border-black rounded-full px-7 py-4 font-bold hover:bg-gray-50 active:scale-[0.98] transition"
                  data-testid="hero-menu-btn"
                >
                  View Menu
                </Link>
              </div>
            </div>
          </div>

          <div className="relative mj-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-gray-100 shadow-[0_16px_48px_rgba(0,0,0,0.12)]">
              <img src={HERO_IMG} alt="Bibimbap" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-[0_16px_48px_rgba(0,0,0,0.12)] p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-black text-white grid place-items-center font-black">30</div>
              <div>
                <div className="text-xs text-gray-500 font-bold uppercase tracking-wider">Avg. Delivery</div>
                <div className="font-display font-bold">under 30 min</div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-y border-gray-100 bg-[#E8E2D2]/30 py-3 overflow-hidden">
          <div className="flex gap-12 whitespace-nowrap mj-marquee font-display font-black text-xl tracking-tight text-gray-800">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex gap-12">
                <span>먹자 Let's Eat</span><span>·</span>
                <span>Fresh Korean Food</span><span>·</span>
                <span>Bibimbap · Bulgogi · Ramyeon</span><span>·</span>
                <span>Right at your doorstep!</span><span>·</span>
                <span>Now serving Dimapur</span><span>·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-2">Featured</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight">Today's favourites</h2>
          </div>
          <Link to="/menu" className="hidden md:inline-flex items-center gap-1 text-sm font-bold hover:underline" data-testid="featured-viewall">
            View full menu <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="-mx-4 md:mx-0 flex md:grid md:grid-cols-3 gap-4 overflow-x-auto hide-scrollbar px-4 md:px-0 snap-x snap-mandatory">
          {featured.map((d, i) => (
            <Link
              to="/menu"
              key={i}
              className="shrink-0 w-[75%] md:w-auto snap-start bg-white border border-gray-100 rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform"
              data-testid={`featured-${i}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                <img src={d.img} alt={d.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4 flex items-center justify-between">
                <div className="font-display font-bold">{d.name}</div>
                <div className="font-black">₹{d.price}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Why */}
      <section className="bg-[#F7F7F7] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-2">Why Mok.Jaa</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight max-w-2xl">Your favourites filled with comfort and love.</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100" data-testid={`why-${i}`}>
                <div className="w-11 h-11 rounded-full bg-black text-white grid place-items-center mb-4">
                  <w.icon className="w-5 h-5" />
                </div>
                <div className="font-display font-bold text-lg">{w.title}</div>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="bg-black text-white rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_30%_20%,#fff,transparent_50%),radial-gradient(circle_at_80%_70%,#fff,transparent_40%)]" aria-hidden />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-gray-400 mb-3">먹자</div>
            <h2 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-none">Hungry yet?</h2>
            <p className="mt-5 text-gray-300 max-w-md mx-auto">Start your order in seconds — no password needed, pay on delivery.</p>
            <Link
              to="/menu"
              className="mt-8 inline-flex bg-white text-black rounded-full px-8 py-4 font-bold hover:bg-gray-200 transition"
              data-testid="cta-order-btn"
            >
              Order Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
